import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useTypedDispatch, useTypedSelector } from '../..'
import ExclamationOutline from '../../icons/ExclamationOutline'
import Phone from '../../icons/Phone'
import SuccessCircle from '../../icons/SuccessCircle'
import {
  DelayNotificationResolution,
  setNotificationStep,
} from '../../reducers/delayNotificationSlice'
import { colors } from '../../Theme'
import { Alert, AlertType } from '../alert/Alert'
import { AlertButton, AlertButtonVariant } from '../alert/AlertButton'
import TransparentButton from '../button/TransparentButton'
import { useDelayNotificationAutoDismiss } from './useDelayNotificationAutoDismiss'

export type DelayNotificationStep =
  | 'previousShiftDelayUnhandled'
  | 'wasDelayDueToEquipmentFault'
  | 'wrongReasonCodeAmendmentNeeded'
  | 'delayHandledThankYou'
  | 'wasFaultReportedToSupport'
  | 'reportFaultByCallingSupport'
  | 'faultReportedThankYou'

type StepButton = {
  labelKey: string
  action: 'step' | 'handled' | 'amendment'
  target?: DelayNotificationStep
  variant: AlertButtonVariant
}

type StepConfig = {
  textKey: string
  buttons: StepButton[]
  type: AlertType
  icon?: React.ReactNode
  secondaryContent?: React.ReactNode
  autoDisappear?: boolean
  resolution?: DelayNotificationResolution
}

const STEPS: Record<DelayNotificationStep, StepConfig> = {
  previousShiftDelayUnhandled: {
    textKey: 'delayNotification.previousShiftDelayUnhandled',
    buttons: [
      {
        labelKey: 'delayNotification.handle',
        action: 'step',
        target: 'wasDelayDueToEquipmentFault',
        variant: 'contained',
      },
    ],
    type: 'warning',
    icon: <ExclamationOutline iconSize={'extraHuge'} fillColor="black" />,
  },
  wasDelayDueToEquipmentFault: {
    textKey: 'delayNotification.wasDelayDueToEquipmentFault',
    buttons: [
      {
        labelKey: 'no',
        action: 'step',
        target: 'wrongReasonCodeAmendmentNeeded',
        variant: 'outlined',
      },
      {
        labelKey: 'yes',
        action: 'step',
        target: 'wasFaultReportedToSupport',
        variant: 'contained',
      },
    ],
    type: 'warning',
  },
  wrongReasonCodeAmendmentNeeded: {
    textKey: 'delayNotification.wrongReasonCodeAmendmentNeeded',
    buttons: [
      {
        labelKey: 'back',
        action: 'step',
        target: 'wasDelayDueToEquipmentFault',
        variant: 'outlined',
      },
      {
        labelKey: 'delayNotification.amendment',
        action: 'amendment',
        variant: 'contained',
      },
    ],
    type: 'success',
  },
  delayHandledThankYou: {
    textKey: 'delayNotification.delayHandledThankYou',
    buttons: [],
    type: 'success',
    autoDisappear: true,
    resolution: 'AMENDMENT',
    secondaryContent: (
      <SuccessCircle
        iconSize={'huge'}
        fillColor={colors.primaryBlue}
        fillColorSecondary={colors.white}
      />
    ),
  },
  wasFaultReportedToSupport: {
    textKey: 'delayNotification.wasFaultReportedToSupport',
    buttons: [
      {
        labelKey: 'no',
        action: 'step',
        target: 'reportFaultByCallingSupport',
        variant: 'outlined',
      },
      {
        labelKey: 'yes',
        action: 'step',
        target: 'faultReportedThankYou',
        variant: 'contained',
      },
    ],
    type: 'warning',
  },
  reportFaultByCallingSupport: {
    textKey: 'delayNotification.reportFaultByCallingSupport',
    buttons: [],
    type: 'warning',
    secondaryContent: <Phone iconSize={'large'} />,
  },
  faultReportedThankYou: {
    textKey: 'delayNotification.delayHandledThankYou',
    buttons: [],
    type: 'success',
    autoDisappear: true,
    resolution: 'FAULT_REPORTED',
    secondaryContent: (
      <SuccessCircle
        iconSize={'huge'}
        fillColor={colors.primaryBlue}
        fillColorSecondary={colors.white}
      />
    ),
  },
}

type Props = {
  notificationKey: string
  initialStep?: DelayNotificationStep
  // Overrides the main text on the initial step (e.g. train + leg + date of an
  // earlier shift's delay, with the explanation shown as a shared list title).
  initialText?: string
  onAmendment?: () => void
}

const DelayNotificationBar = ({
  notificationKey,
  initialStep,
  initialText,
  onAmendment,
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useTypedDispatch()
  const step = useTypedSelector((state) => state.delayNotifications.notifications[notificationKey])
  const isAlreadyHandled = useTypedSelector(
    (state) => !!state.delayNotifications.handledNotifications[notificationKey]
  )
  const supportPhone = useTypedSelector(
    (state) => state.configurations.data.phoneKuljettajienTukipalvelu
  )
  const config = step ? STEPS[step] : undefined
  const { isGone, startExit, wasTransitioned } = useDelayNotificationAutoDismiss({
    notificationKey,
    autoDisappear: !!config?.autoDisappear,
    resolution: config?.resolution,
  })

  const handleButtonClick = useCallback(
    (button: StepButton) => {
      switch (button.action) {
        case 'step':
          if (button.target) {
            dispatch(setNotificationStep({ key: notificationKey, step: button.target }))
          }
          break
        case 'handled':
          startExit()
          break
        case 'amendment':
          dispatch(setNotificationStep({ key: notificationKey, step: 'delayHandledThankYou' }))
          onAmendment?.()
          break
      }
    },
    [dispatch, notificationKey, onAmendment, startExit]
  )

  // If the notification was already handled by another component instance (e.g., on a
  // different page) and this instance never participated in the step transition, hide it.
  if (isAlreadyHandled && !wasTransitioned) {
    return null
  }

  if (!step || isGone || !config) {
    return null
  }

  return (
    <Alert
      text={step === initialStep && initialText ? initialText : t(config.textKey)}
      secondaryText={step === 'reportFaultByCallingSupport' ? supportPhone : undefined}
      type={config.type}
      icon={config.icon}
    >
      {config.buttons.map((button) => (
        <AlertButton
          key={button.labelKey}
          variant={button.variant}
          onClick={() => handleButtonClick(button)}
        >
          {t(button.labelKey)}
        </AlertButton>
      ))}
      {config.secondaryContent}
      {step === 'reportFaultByCallingSupport' && supportPhone && (
        <TransparentButton
          spacing="none"
          onClick={() => {
            window.open(`tel:${supportPhone.replace(/\s/g, '')}`, '_self')
            dispatch(
              setNotificationStep({ key: notificationKey, step: 'wasFaultReportedToSupport' })
            )
          }}
        >
          {t('delayNotification.call').toUpperCase()}
        </TransparentButton>
      )}
    </Alert>
  )
}

DelayNotificationBar.displayName = 'DelayNotificationBar'

export default DelayNotificationBar
